import { CustomColumnProps } from '@ihsm/ui-shared';
import { has } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useGetReportSummaryDataQuery } from 'services/newApi/reportsDataApi';
import { numberRoundDisplayFormatter } from 'shared/utils/numberDisplay';
import { useAppSelector } from 'state/store';
import { currentReportSelector } from 'state/store/selectors';

type UseReportsSummaryData = {
  data: Record<string, string>[];
  columnList: CustomColumnProps[];
  fundsList: string[];
  showLoading: boolean;
  selectedFund: string;
  setSelectedFund: Dispatch<SetStateAction<string>>;
  isError: boolean;
};

export const BASE_COLUMNS: CustomColumnProps[] = [
  { name: 'fundName', title: 'Fund Name' },
  { name: 'reportingCurrency', title: 'Currency' },
  { name: 'benchmarkName', title: 'Benchmark' },
];

export const useReportsSummaryData = (): UseReportsSummaryData => {
  const [skip, setSkip] = useState<boolean>(true);
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const selectedContext = useAppSelector(
    (state) => state.contextsState.selectedContext
  );

  const currentReport = currentReportSelector(useAppSelector((state) => state));

  const {
    data: summaryDataList = [],
    isError,
    isLoading,
  } = useGetReportSummaryDataQuery(
    {
      selectedContext,
      selectedReport: currentReport?.name ?? '',
    },
    { skip }
  );

  const columnList = useMemo(() => {
    const result = [...BASE_COLUMNS];

    if (
      summaryDataList &&
      summaryDataList?.length &&
      currentReport &&
      currentReport.columns.length > 0
    ) {
      const sorted = [...currentReport.columns].sort((a, b) => a.sort - b.sort);

      sorted.forEach((column) => {
        const firstElement = summaryDataList[0];

        if (has(firstElement.results, column.name)) {
          result.push({ name: column.name, title: column.name });
        }
      });
    }

    return result;
  }, [currentReport, summaryDataList]);

  const fundsList = useMemo(() => {
    return summaryDataList.map(({ fundName }) => {
      return fundName;
    });
  }, [summaryDataList]);

  const data = useMemo(() => {
    return summaryDataList.map(
      ({ fundName, benchmarkName, reportingCurrency, results }) => {
        const resultsFormatted = { ...results };

        Object.keys(resultsFormatted).forEach((key) => {
          resultsFormatted[key] = numberRoundDisplayFormatter(results[key]);
        });

        return {
          reportingCurrency,
          fundName,
          benchmarkName,
          columnType: 'currency',
          ...resultsFormatted,
        };
      }
    );
  }, [summaryDataList]);

  useEffect(() => {
    if (selectedContext && currentReport && skip) {
      setSkip(false);
      setShowLoading(true);
    }
  }, [selectedContext, currentReport, skip, setSkip, setShowLoading]);

  useEffect(() => {
    if ((summaryDataList?.length && !isLoading) || isError) {
      if (!isError) setSelectedFund(summaryDataList[0].fundName);
      setShowLoading(false);
    }
  }, [isError, isLoading, summaryDataList, setShowLoading]);

  useEffect(() => {
    if (selectedContext && currentReport && skip) {
      setSkip(false);
      setShowLoading(true);
    }
  }, [selectedContext, currentReport, skip, setSkip, setShowLoading]);

  return {
    showLoading,
    isError,
    data,
    fundsList,
    selectedFund,
    setSelectedFund,
    columnList,
  };
};
